import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { refreshSession } from '@/lib/api';
import { ACTIVITY_KEY, touchActivity, useAuth, useMe } from '@/lib/auth';

/** Show the warning this long before the inactivity sign-out (at most a quarter of the timeout). */
const WARN_MS = 2 * 60_000;

function readActivity(): number {
  try {
    const v = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : Date.now();
  } catch {
    return Date.now();
  }
}

/**
 * Inactivity warning. When the organization signs people out after N idle minutes, a dialog appears two
 * minutes before with a countdown and a "Stay signed in" button, which records activity and renews the
 * session. The actual sign-out still happens in the auth provider; this only gives people a chance to stay.
 */
export function SessionWatch() {
  const me = useMe();
  const { logout } = useAuth();
  const idleMinutes = me.tenant.security?.idleTimeoutMinutes ?? 0;
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    if (idleMinutes <= 0) {
      setRemainingMs(null);
      return;
    }
    const limit = idleMinutes * 60_000;
    const warnAt = Math.min(WARN_MS, limit / 4);
    const tick = () => {
      const left = limit - (Date.now() - readActivity());
      setRemainingMs(left <= warnAt ? Math.max(0, left) : null);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [idleMinutes]);

  if (remainingMs === null) return null;
  const mm = Math.floor(remainingMs / 60_000);
  const ss = Math.floor((remainingMs % 60_000) / 1000);

  const stay = async () => {
    touchActivity();
    setRemainingMs(null);
    await refreshSession();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && void stay()}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Still there?</DialogTitle>
          <DialogDescription>
            For your organization&rsquo;s security you are signed out after {idleMinutes} minutes without activity. You will be signed out in{' '}
            <span className="text-foreground font-medium tabular-nums" aria-live="polite">
              {mm}:{String(ss).padStart(2, '0')}
            </span>
            .
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => void logout('user')}>
            Sign out now
          </Button>
          <Button autoFocus onClick={() => void stay()}>
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
